// Path: src/components/dev/utils/ErrorBoundary.tsx
// Updated to include SEO-friendly fallback UI with CTA to /OLSStudentProspect
// Improved styling for radiant aesthetic and mobile responsiveness
// Added debug logs for error tracking
// 06/02/25 09:11 AM +07
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Result } from "antd";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #fff5e6;
  padding: 20px;
  text-align: center;
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.error("ErrorBoundary caught error:", error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary detailed error:", error, "Component stack:", errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      console.log("ErrorBoundary rendering - error:", this.state.error?.message, "stack:", this.state.errorInfo?.componentStack);
      return (
        <ErrorContainer>
          <Helmet>
            <title>Error | OneLightSystem - Sun Light Meditation</title>
            <meta
              name="description"
              content="An error occurred on OneLightSystem. Explore Sun Light Meditation and join OLS at olsme.com/OLSStudentProspect."
            />
            <meta
              name="keywords"
              content="Sun Light Meditation, OLS meditation education, join OLS"
            />
            <meta property="og:title" content="Error | OneLightSystem" />
            <meta
              property="og:description"
              content="An error occurred. Join OLS’s Sun Light Meditation journey at olsme.com/OLSStudentProspect."
            />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://www.olsme.com" />
            <meta property="og:image" content="https://www.olsme.com/public/logo.png" />
            <meta property="twitter:card" content="summary_large_image" />
          </Helmet>
          <Result
            status="error"
            title="Something Went Wrong"
            subTitle="We’re sorry, an error occurred. Let’s get you back to your Sun Light Meditation journey!"
            extra={[
              <Button
                type="primary"
                onClick={() => window.location.reload()}
                key="reload"
                aria-label="Reload page"
              >
                Reload Page
              </Button>,
              <Button
                type="default"
                href="/OLSStudentProspect"
                key="join"
                aria-label="Join OLS"
                style={{ background: "#ffcc00", color: "#0427f4" }}
              >
                Join OLS Now
              </Button>,
            ]}
          />
        </ErrorContainer>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;